/**
 * WifiTopologyDoc — Borrador de documentación del WiFi generado desde la
 * topología, y panel para revisarlo.
 *
 * La idea es no escribir el inventario a mano: se recorre lo que hay cargado en
 * la CMDB (controladores, APs, SSIDs, RADIUS, cableado y planos) y se redacta un
 * documento base que luego se edita en el editor de documentación de la CMDB.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import './WifiTopologyDoc.css';

// ── Utilidades de texto ───────────────────────────────────────────────────────

const esc = s => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Markdown del borrador → HTML sencillo (h1..h3, listas y párrafos) para TipTap. */
export function draftToHtml(md) {
  const out = [];
  let inList = false;
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };
  md.split('\n').forEach(raw => {
    const line = raw.replace(/\s+$/, '');
    const h  = /^(#{1,3})\s+(.*)$/.exec(line);
    const li = /^-\s+(.*)$/.exec(line);
    if (h)  { closeList(); const n = h[1].length; out.push(`<h${n}>${esc(h[2])}</h${n}>`); return; }
    if (li) { if (!inList) { out.push('<ul>'); inList = true; } out.push(`<li>${esc(li[1])}</li>`); return; }
    closeList();
    if (line.trim()) out.push(`<p>${esc(line)}</p>`);
  });
  closeList();
  return out.join('');
}

const isEnterprise = s => String(s?.security_mode || '').includes('enterprise');
const slug = s => String(s || 'empresa').toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function apLocation(ap) {
  const parts = [];
  if (ap.location_room) parts.push(ap.location_room);
  else if (ap.location_name) parts.push(ap.location_name);
  if (ap.location_floor) parts.push(`planta ${ap.location_floor}`);
  return parts.length ? parts.join(' · ') : 'sin ubicación';
}

function apCable(ap) {
  const up = ap.uplink;
  if (!up) return 'sin cablear';
  const hops = (up.hops || []).map(h => `${h.element_name || ''} ${h.label || ''}`.trim()).filter(Boolean);
  return [`${up.port_label}${up.speed_mbps ? ` (${up.speed_mbps} Mbps)` : ''}`, ...hops].join(' → ') || 'sin cablear';
}

// ── Generador del borrador ────────────────────────────────────────────────────

export function buildWifiDraft({ controllers = [], aps = [], ssids = [], radiusServers = [], services = [], companyName }) {
  const fecha = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  const L = [];
  const p = (s = '') => L.push(s);
  const byId = id => aps.find(a => a.id === id);
  const ctrlById = id => controllers.find(c => c.id === id);
  const svcName = rs => {
    if (!rs.service_id) return null;
    const s = services.find(x => x.id === rs.service_id);
    return s ? (s.display_name || s.name) : `servicio #${rs.service_id}`;
  };
  const ssidsOfCtrl = id => ssids.filter(s => s.controller_id === id);
  const ssidsOfAp = id => ssids.filter(s => s.access_point_id === id);

  // ── 1. Resumen
  const activos = aps.filter(a => a.status === 'active').length;
  const enterprise = ssids.filter(isEnterprise).length;
  const colocados = aps.filter(a => a.x_pos != null && a.y_pos != null).length;
  const cableados = aps.filter(a => a.uplink).length;

  p('# WiFi · documento de configuración e inventario');
  p();
  p(`_${companyName ? companyName + ' · ' : ''}borrador generado automáticamente el ${fecha} desde la topología de la CMDB. Revísalo, corrígelo y publícalo cuando esté listo._`);
  p();
  p('## 1 · Resumen');
  p();
  p(`- Controladores WiFi: ${controllers.length}`);
  p(`- Puntos de acceso: ${aps.length} (${activos} en estado activo)`);
  p(`- Redes WiFi (SSID): ${ssids.length} · ${enterprise} con seguridad enterprise (RADIUS)`);
  p(`- Servidores RADIUS: ${radiusServers.length}`);
  p(`- Cableado: ${cableados} de ${aps.length} APs con cable registrado`);
  p(`- Ubicados en plano: ${colocados} de ${aps.length} APs`);
  p();

  // ── 2. Controladores
  p('## 2 · Controladores');
  p();
  if (!controllers.length) {
    p('- No hay controladores dados de alta.');
    p();
  }
  controllers.forEach(c => {
    const cAps = aps.filter(a => a.controller_id === c.id);
    const cSsids = ssidsOfCtrl(c.id);
    p(`### ${c.name}${c.status_label ? ` — ${c.status_label.toLowerCase()}` : ''}`);
    p();
    p(`- Equipo: ${[c.vendor_label || c.vendor, c.model].filter(Boolean).join(' ') || '—'}${c.firmware_version ? ` · firmware ${c.firmware_version}` : ''}`);
    p(`- Gestión: ${c.management_ip || 'sin IP'}${c.hostname ? ` · ${c.hostname}` : ''}`);
    if (c.rack_name || c.rack_element_name) {
      p(`- Ubicación: ${[c.rack_name && (c.rack_position_u != null ? `${c.rack_name} (U${c.rack_position_u})` : c.rack_name), c.rack_element_name].filter(Boolean).join(' · ')}`);
    }
    if (c.max_aps || c.max_clients) {
      p(`- Capacidad: ${c.max_aps || '—'} APs y ${c.max_clients || '—'} clientes`);
    }
    p(`- APs gestionados (${cAps.length}): ${cAps.map(a => a.name).join(', ') || 'ninguno'}`);
    p(`- SSIDs que difunde (${cSsids.length}): ${cSsids.map(s => s.name).join(', ') || 'ninguno'}`);
    if (c.notes) p(`- Notas: ${c.notes}`);
    p();
  });

  // ── 3. Puntos de acceso
  p('## 3 · Puntos de acceso');
  p();
  if (!aps.length) {
    p('- No hay puntos de acceso dados de alta.');
    p();
  }
  aps.forEach(a => {
    const propias = ssidsOfAp(a.id);
    const heredadas = a.controller_id ? ssidsOfCtrl(a.controller_id) : [];
    p(`### ${a.name}${a.status_label ? ` — ${a.status_label.toLowerCase()}` : ''}`);
    p();
    p(`- Equipo: ${[a.vendor_label || a.vendor, a.model].filter(Boolean).join(' ') || '—'}`);
    p(`- Gestión: ${a.management_ip || 'sin IP'}${a.mac_address ? ` · MAC ${a.mac_address}` : ''}`);
    p(`- Radio: ${[a.wifi_standard_label || a.wifi_standard, a.band_label || a.band, a.tx_power_dbm ? `${a.tx_power_dbm} dBm` : null, a.poe_powered ? 'alimentado por PoE' : null].filter(Boolean).join(' · ')}`);
    p(`- Montaje: ${a.mount_type_label || a.mount_type || '—'}`);
    p(`- Ubicación: ${apLocation(a)}`);
    p(`- Controlador: ${a.controller_id ? (a.controller_name || ctrlById(a.controller_id)?.name || `#${a.controller_id}`) : 'sin controlador (gestiona sus propios SSIDs)'}`);
    p(`- Cableado: ${apCable(a)}`);
    if (a.x_pos != null && a.y_pos != null) {
      p(`- Plano: colocado en ${apLocation(a)} (${(a.x_pos * 100).toFixed(1)}% / ${(a.y_pos * 100).toFixed(1)}%)`);
    } else if (a.location_id) {
      p('- Plano: asignado a una ubicación pero todavía sin colocar en el plano');
    }
    if (propias.length) p(`- SSIDs propios (${propias.length}): ${propias.map(s => s.name).join(', ')}`);
    if (heredadas.length) p(`- SSIDs heredados del controlador (${heredadas.length}): ${heredadas.map(s => s.name).join(', ')}`);
    if (a.notes) p(`- Notas: ${a.notes}`);
    p();
  });

  // ── 4. SSIDs
  p('## 4 · Redes WiFi (SSID)');
  p();
  if (!ssids.length) {
    p('- No hay SSIDs definidos.');
    p();
  }
  ssids.forEach(s => {
    const dueno = s.controller_id
      ? `controlador ${ctrlById(s.controller_id)?.name || '#' + s.controller_id} (lo emiten todos sus APs)`
      : (s.access_point_id ? `AP ${byId(s.access_point_id)?.name || '#' + s.access_point_id}` : 'sin asignar');
    p(`### ${s.name} — ${s.security_mode_label || s.security_mode}`);
    p();
    p(`- Difundido por: ${dueno}`);
    p(`- VLAN: ${s.vlan_id || 'sin VLAN'} · Bandas: ${s.band_filter_label || s.band_filter || 'todas'}`);
    p(`- Red oculta: ${s.hidden ? 'sí' : 'no'} · Aislamiento de clientes: ${s.client_isolation ? 'sí' : 'no'}`);
    if (isEnterprise(s)) {
      p(`- Autenticación: RADIUS ${s.radius_server_name ? `${s.radius_server_name} ` : ''}(${s.radius_server || 'sin host'}:${s.radius_port || 1812}${s.radius_acct_port ? ` / accounting ${s.radius_acct_port}` : ''})`);
    } else if (s.security_mode === 'open') {
      p('- Autenticación: red abierta, sin contraseña');
    }
    if (s.notes) p(`- Notas: ${s.notes}`);
    p();
  });

  // ── 5. RADIUS
  p('## 5 · Servidores RADIUS');
  p();
  if (!radiusServers.length) {
    p('- No hay servidores RADIUS dados de alta.');
    p();
  }
  radiusServers.forEach(rs => {
    const usan = ssids.filter(s => s.radius_server_id === rs.id);
    p(`### ${rs.name} — ${rs.host}`);
    p();
    p(`- Puertos: autenticación ${rs.auth_port || 1812} UDP · accounting ${rs.acct_port || 1813} UDP`);
    p(`- Estado: ${rs.status === 'active' ? 'activo' : rs.status}`);
    p(`- Servicio CMDB: ${svcName(rs) || 'sin servicio vinculado'}`);
    p(`- SSIDs que lo usan (${usan.length}): ${usan.map(s => s.name).join(', ') || 'ninguno'}`);
    p('- Secreto compartido: no se guarda en la CMDB (se configura en el controlador y en los APs).');
    if (rs.description) p(`- Descripción: ${rs.description}`);
    p();
  });

  // ── 6. Cableado
  p('## 6 · Cableado de los APs');
  p();
  if (!aps.length) {
    p('- Sin APs.');
    p();
  } else {
    aps.forEach(a => p(`- ${a.name}: ${apCable(a)}`));
    p();
  }

  // ── 7. Planos
  p('## 7 · Ubicación en los planos de planta');
  p();
  const porUbicacion = {};
  aps.forEach(a => {
    const key = a.location_id ? apLocation(a) : 'sin ubicación asignada';
    if (!porUbicacion[key]) porUbicacion[key] = { total: 0, colocados: 0 };
    porUbicacion[key].total += 1;
    if (a.x_pos != null && a.y_pos != null) porUbicacion[key].colocados += 1;
  });
  Object.entries(porUbicacion).forEach(([k, v]) => p(`- ${k}: ${v.colocados} de ${v.total} APs colocados sobre el plano`));
  p();

  // ── 8. Puntos a revisar
  const revisar = [];
  const sinCtrl = aps.filter(a => !a.controller_id);
  if (sinCtrl.length) revisar.push(`${sinCtrl.length} AP(s) sin controlador asignado: ${sinCtrl.map(a => a.name).join(', ')}`);
  const sinUbic = aps.filter(a => !a.location_id);
  if (sinUbic.length) revisar.push(`${sinUbic.length} AP(s) sin ubicación (no se pueden colocar en un plano): ${sinUbic.map(a => a.name).join(', ')}`);
  const sinCable = aps.filter(a => !a.uplink);
  if (sinCable.length) revisar.push(`${sinCable.length} AP(s) sin cable registrado: ${sinCable.map(a => a.name).join(', ')}`);
  const sinPlano = aps.filter(a => a.location_id && (a.x_pos == null || a.y_pos == null));
  if (sinPlano.length) revisar.push(`${sinPlano.length} AP(s) con ubicación pero aún sin colocar en el plano: ${sinPlano.map(a => a.name).join(', ')}`);
  const entSinRadius = ssids.filter(s => isEnterprise(s) && !s.radius_server_id && !s.radius_server);
  if (entSinRadius.length) revisar.push(`${entSinRadius.length} SSID(s) enterprise sin servidor RADIUS asignado: ${entSinRadius.map(s => s.name).join(', ')}`);
  const huerfanos = ssids.filter(s => !s.controller_id && !s.access_point_id);
  if (huerfanos.length) revisar.push(`${huerfanos.length} SSID(s) sin difusor (ni controlador ni AP): ${huerfanos.map(s => s.name).join(', ')}`);
  const ctrlSinAps = controllers.filter(c => !aps.some(a => a.controller_id === c.id));
  if (ctrlSinAps.length) revisar.push(`${ctrlSinAps.length} controlador(es) sin APs asociados: ${ctrlSinAps.map(c => c.name).join(', ')}`);
  const ctrlSinSsid = controllers.filter(c => ssidsOfCtrl(c.id).length === 0);
  if (ctrlSinSsid.length) revisar.push(`${ctrlSinSsid.length} controlador(es) sin SSIDs definidos: ${ctrlSinSsid.map(c => c.name).join(', ')}`);
  const radiusSinServicio = radiusServers.filter(rs => !rs.service_id);
  if (radiusSinServicio.length) revisar.push(`${radiusSinServicio.length} servidor(es) RADIUS sin servicio CMDB vinculado: ${radiusSinServicio.map(rs => rs.name).join(', ')}`);
  const radiusSinUso = radiusServers.filter(rs => !ssids.some(s => s.radius_server_id === rs.id));
  if (radiusSinUso.length) revisar.push(`${radiusSinUso.length} servidor(es) RADIUS que no usa ningún SSID: ${radiusSinUso.map(rs => rs.name).join(', ')}`);
  const noActivos = aps.filter(a => a.status && a.status !== 'active');
  if (noActivos.length) revisar.push(`${noActivos.length} AP(s) que no están activos: ${noActivos.map(a => `${a.name} (${a.status_label || a.status})`).join(', ')}`);

  p('## 8 · Puntos a revisar');
  p();
  if (revisar.length) revisar.forEach(r => p(`- ${r}`));
  else p('- Sin incidencias detectadas automáticamente.');
  p();
  p('## 9 · Notas y cambios');
  p();
  p('- (añade aquí el histórico de cambios, incidencias conocidas, pendientes….)');

  return L.join('\n');
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export default function WifiTopologyDoc({
  companyId, controllers, aps, ssids, radiusServers, services, onClose, onNavigate,
}) {
  const [companyName, setCompanyName] = useState('');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.getAuthorizedCompanies()
      .then(list => {
        const c = (list || []).find(x => String(x.id) === String(companyId));
        if (c) setCompanyName(c.name);
      })
      .catch(() => {});
  }, [companyId]);

  const draft = useMemo(
    () => buildWifiDraft({ controllers, aps, ssids, radiusServers, services, companyName }),
    [controllers, aps, ssids, radiusServers, services, companyName]
  );

  const fecha = new Date().toISOString().slice(0, 10);
  const fileBase = `wifi-${slug(companyName)}-${fecha}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { setError('No se pudo copiar al portapapeles.'); }
  };

  const handleDownload = () => {
    const url = URL.createObjectURL(new Blob([draft], { type: 'text/markdown;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${fileBase}.md`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    setBusy(true); setError(null);
    try {
      const doc = await api.createDocPage(companyId, {
        title: `WiFi · configuración e inventario${companyName ? ` (${companyName})` : ''}`,
        description: 'Borrador generado desde la topología WiFi de la CMDB',
        doc_type: 'richtext',
        status: 'draft',
        content_html: draftToHtml(draft),
        tags: ['wifi', 'topologia', 'borrador'],
      });
      if (!doc?.id) { onClose(); return; }

      // Se vincula a todos los elementos del WiFi para que el documento aparezca
      // en el botón de documentación de cada ficha (controlador, AP, RADIUS y SSID).
      const destinos = [
        ...controllers.map(c => ['wifi_controller', c.id, c.name]),
        ...aps.map(a => ['wifi_ap', a.id, a.name]),
        ...radiusServers.map(r => ['radius_server', r.id, r.name]),
        ...ssids.map(s => ['wifi_ssid', s.id, s.name]),
      ];
      const resultados = await Promise.allSettled(
        destinos.map(([type, id, name]) => api.addDocPageLink(doc.id, companyId, type, id, name))
      );
      const fallidos = resultados.filter(r => r.status === 'rejected').length;
      if (fallidos) setError(`Documento creado, pero ${fallidos} vínculo(s) no se pudieron guardar.`);

      onNavigate(`/documentation/${doc.id}`);
    } catch (e) { setError(e.message || 'No se pudo crear el documento'); }
    finally { setBusy(false); }
  };

  const lineas = draft.split('\n').length;

  return (
    <aside className="wtopo-aside wtopo-doc">
      <div className="wtopo-inner">
        <div className="wtopo-head">
          <span className="wtopo-icon" style={{ background: 'rgba(45,212,191,.2)' }}>📄</span>
          <div className="wtopo-title">
            <h3>Documentación del WiFi</h3>
            <p>Borrador generado con lo que hay configurado · {lineas} líneas</p>
          </div>
          <button className="wtopo-x" onClick={onClose} title="Cerrar">✕</button>
        </div>

        <p className="wtopo-hint">
          Se redacta solo con lo que hay en la CMDB (controladores, APs, SSIDs, RADIUS, cableado y planos) e
          incluye una sección de puntos a revisar. Puedes copiarlo, descargarlo o guardarlo como documento
          editable para retocarlo: al guardarlo queda vinculado a cada controlador, AP, SSID y servidor
          RADIUS, así aparece en el botón de documentación de todas sus fichas.
        </p>

        <div className="wtopo-actions">
          <button className="wtopo-btn" onClick={handleCopy}>📋 {copied ? '¡Copiado!' : 'Copiar'}</button>
          <button className="wtopo-btn" onClick={handleDownload}>⬇ Descargar .md</button>
          <button className="wtopo-btn wtopo-btn-primary" onClick={handleSave} disabled={busy}
            title="Crea el documento y lo vincula a los controladores, APs, SSIDs y servidores RADIUS">
            {busy ? 'Guardando…' : '💾 Guardar como documento editable'}
          </button>
          <button className="wtopo-btn" onClick={onClose}>Cerrar</button>
        </div>

        {error && <div className="wtopo-error">⚠️ {error}</div>}

        <pre className="wtopo-doc-pre">{draft}</pre>
      </div>
    </aside>
  );
}
